<template>
  <GenericCard
    :locked="locked"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="v$.section.$invalid"
    :dirty="v$.section.$anyDirty"
    :create="create"
    title="Eligibility Guide"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col>
          <v-row key="0">
            <v-col class="text-center">
              <v-alert
                border="left"
                colored-border
                type="warning"
                icon="mdi-alert-octagon-outline"
                elevation="2"
                class="mb-10"
              >
                This eligibility guide is intended for lawyers and is <strong>not a substitute for independent legal advice</strong> supplied by a lawyer familiar with a client’s case.
              </v-alert>

              <div class="font-weight-bold">
                Asylum protects individuals who have been or would be persecuted in their countries of origin
              </div>

              <div>All these elements must be present.</div>
            </v-col>
          </v-row>

          <v-row key="1">
            <v-col
              cols="12"
              sm="6"
            >
              <v-checkbox
                v-model="section.harm_rising"
                hide-details
              >
                <div slot="label">
                  <span class="checkbox-label-type font-weight-bold">Harm:</span> Harm rising to the level of persecution. Persecution is generally defined as "objectively offensive harm". It must be considered cumulatively. Objectively offensive standard is lower for children experiencing harm. The harm may have occurred in the past or there is a well-founded fear of it happening in the future
                </div>
              </v-checkbox>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-checkbox
                v-model="section.harm_motive"
                hide-details
              >
                <div slot="label">
                  <span
                    data-v-169911c4=""
                    class="checkbox-label-type font-weight-bold"
                  >Motive:</span> Why will the actor cause the harm? Asylum and withholding protect individuals when harm occurs on account of race, religion, nationality, political opinion, or membership in a particular social group. If an actor has mixed motives to cause the harm, a protected ground must be at least one central reason.
                </div>
              </v-checkbox>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-checkbox
                v-model="section.harm_agent"
                hide-details
              >
                <div slot="label">
                  <span class="checkbox-label-type font-weight-bold">Agent:</span> Who caused or will cause the harm?
                </div>
              </v-checkbox>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-checkbox
                v-model="section.failure_of_state"
                hide-details
              >
                <div slot="label">
                  Failure of state protection because the State is the actor or the State is unable or unwilling to control the actor
                </div>
              </v-checkbox>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-checkbox
                v-model="section.internal_relocation"
                hide-details
              >
                <div slot="label">
                  Internal Relocation is unsafe or unreasonable
                </div>
              </v-checkbox>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-checkbox
                v-model="section.filed_within_one_year"
                hide-details
              >
                <div slot="label">
                  Filed within one year or exception applies
                </div>
              </v-checkbox>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-checkbox
                v-model="section.no_crime_bars"
                hide-details
              >
                <div slot="label">
                  No crime bars
                </div>
              </v-checkbox>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-checkbox
                v-model="section.no_national_security"
                hide-details
              >
                <div slot="label">
                  No national security bars
                </div>
              </v-checkbox>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-checkbox
                v-model="section.no_firm_resettlement"
                hide-details
              >
                <div slot="label">
                  No firm resettlement bars
                </div>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { alertHandler } from '@/alertHandler'

import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'EligibilityGuideCard',
  components: { GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      section: {},
    }
  },

  validations () {
    return {
      section: {},
    }
  },

  computed: {
    cardIdentifier () { return `eligibility-guide-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },

    locked: {
      get () {
        return this.peEligibilityGuideCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeEligibilityGuideCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    sectionIdentifier () { return this.section.id || 'new' },

    ...mapGetters(PARTY_NAMESPACE, [
      'peEligibilityGuideCardLock',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPeEligibilityGuideCard(this.sectionIdentifier)
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peEligibilityGuideCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peEligibilityGuideCardCreateUndo()
      else {
        return this.peEligibilityGuideCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peEligibilityGuideCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peEligibilityGuideCardUpdate({
        id: this.section.id,
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    async onSectionUnlock () {
      return this.setPeEligibilityGuideCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peEligibilityGuideCardCreateUndo',
      'setPeEligibilityGuideCardLock',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeEligibilityGuideCard',
      'peEligibilityGuideCardCreate',
      'peEligibilityGuideCardDelete',
      'peEligibilityGuideCardRefresh',
      'peEligibilityGuideCardUpdate',
    ]),
  },
}
</script>
